import axios from 'axios';
import { postWithAiBilling, isBillingUiEnabled } from '../../billing/billingApi';
import { getMinutesBetween } from '../utils/dateUtils';

const CANDIDATE_SLOTS = [
  ['08:00', '09:00'],
  ['10:00', '11:00'],
  ['16:00', '17:00'],
  ['19:00', '20:00'],
  ['20:30', '21:30'],
];

const overlaps = (slot, plan) => slot[0] < plan.endTime && slot[1] > plan.startTime;

const toTime = (minutes) => {
  const hour = String(Math.floor(minutes / 60)).padStart(2, '0');
  const minute = String(minutes % 60).padStart(2, '0');
  return `${hour}:${minute}`;
};

const findAvailableSlot = (plans, preferredSlot) => {
  const scanSlots = [60, 30].flatMap((duration) => (
    Array.from(
      { length: Math.floor(((23 * 60) - (8 * 60) - duration) / 30) + 1 },
      (_, index) => {
        const start = (8 * 60) + (index * 30);
        return [toTime(start), toTime(start + duration)];
      },
    )
  ));
  const candidates = preferredSlot
    ? [preferredSlot, ...CANDIDATE_SLOTS, ...scanSlots]
    : [...CANDIDATE_SLOTS, ...scanSlots];
  return candidates.find((candidate) => (
    Array.isArray(candidate)
    && candidate.length === 2
    && getMinutesBetween(candidate[0], candidate[1]) > 0
    && !plans.some((plan) => overlaps(candidate, plan))
  )) || ['21:30', '22:30'];
};

const createFallbackDraft = (plans, studyDate) => {
  const incompletePlan = plans.find((plan) => plan.status !== 'completed');
  const slot = findAvailableSlot(plans);

  if (incompletePlan) {
    return {
      studyDate,
      startTime: slot[0],
      endTime: slot[1],
      subject: incompletePlan.subject,
      planContent: `${incompletePlan.subject} 미완료 학습 보완 및 핵심 내용 복습`,
      status: 'planned',
      understanding: 0,
      actualMinutes: 0,
      actualContent: '',
      memo: 'AI 추천 초안입니다. 내용을 확인한 뒤 저장하세요.',
      isAiDraft: true,
      aiSource: 'fallback',
    };
  }

  return {
    studyDate,
    startTime: slot[0],
    endTime: slot[1],
    subject: '복습',
    planContent: '오늘 배운 내용 핵심 정리와 오답 복습',
    status: 'planned',
    understanding: 0,
    actualMinutes: 0,
    actualContent: '',
    memo: 'AI 추천 초안입니다. 과목과 내용을 수정할 수 있습니다.',
    isAiDraft: true,
    aiSource: 'fallback',
  };
};

const normalizeAiDraft = (rawPlan, plans, studyDate) => {
  if (!rawPlan || typeof rawPlan !== 'object') return null;

  const preferredSlot = [String(rawPlan.startTime || ''), String(rawPlan.endTime || '')];
  const slot = findAvailableSlot(plans, preferredSlot);
  const subject = String(rawPlan.subject || '').trim().slice(0, 100);
  const planContent = String(rawPlan.planContent || '').trim().slice(0, 1000);
  if (!subject || !planContent) return null;

  return {
    studyDate,
    startTime: slot[0],
    endTime: slot[1],
    subject,
    planContent,
    status: 'planned',
    understanding: 0,
    actualMinutes: 0,
    actualContent: '',
    memo: String(rawPlan.memo || 'AI 추천 초안입니다. 내용을 확인한 뒤 저장하세요.').trim().slice(0, 1000),
    isAiDraft: true,
    aiSource: 'ai',
  };
};

const toAiPlanContext = (plans) => plans.slice(0, 30).map((plan) => ({
  startTime: plan.startTime,
  endTime: plan.endTime,
  subject: plan.subject,
  planContent: plan.planContent,
  status: plan.status,
  understanding: Number(plan.understanding) || 0,
}));

const summarizeLearningHistory = (historyPlans, studyDate) => {
  const plans = Array.isArray(historyPlans) ? historyPlans : [];
  const subjectMap = new Map();
  const recordedDates = new Set();

  plans.forEach((plan) => {
    const subject = String(plan.subject || '기타').trim() || '기타';
    const studyDateKey = String(plan.studyDate || '').slice(0, 10);
    if (studyDateKey) recordedDates.add(studyDateKey);
    const current = subjectMap.get(subject) || {
      subject,
      totalCount: 0,
      completedCount: 0,
      unfinishedCount: 0,
      plannedMinutes: 0,
      actualMinutes: 0,
      understandingTotal: 0,
      understandingCount: 0,
    };
    const understanding = Number(plan.understanding) || 0;
    current.totalCount += 1;
    current.completedCount += plan.status === 'completed' ? 1 : 0;
    current.unfinishedCount += plan.status === 'completed' ? 0 : 1;
    current.plannedMinutes += Number(plan.expectedMinutes) || 0;
    current.actualMinutes += Number(plan.actualMinutes) || 0;
    if (understanding > 0) {
      current.understandingTotal += understanding;
      current.understandingCount += 1;
    }
    subjectMap.set(subject, current);
  });

  const subjects = [...subjectMap.values()].map((subject) => ({
    subject: subject.subject,
    totalCount: subject.totalCount,
    completedCount: subject.completedCount,
    unfinishedCount: subject.unfinishedCount,
    completionRate: subject.totalCount ? Math.round((subject.completedCount / subject.totalCount) * 100) : 0,
    plannedMinutes: subject.plannedMinutes,
    actualMinutes: subject.actualMinutes,
    averageUnderstanding: subject.understandingCount
      ? Math.round((subject.understandingTotal / subject.understandingCount) * 10) / 10
      : 0,
  })).sort((a, b) => (
    b.unfinishedCount - a.unfinishedCount
    || a.averageUnderstanding - b.averageUnderstanding
    || b.plannedMinutes - a.plannedMinutes
  ));

  const attentionPlans = plans
    .filter((plan) => plan.status !== 'completed' || (Number(plan.understanding) > 0 && Number(plan.understanding) <= 2))
    .slice(-10)
    .reverse()
    .map((plan) => ({
      studyDate: String(plan.studyDate || '').slice(0, 10),
      subject: String(plan.subject || '').slice(0, 100),
      planContent: String(plan.planContent || '').slice(0, 300),
      status: plan.status,
      understanding: Number(plan.understanding) || 0,
      plannedMinutes: Number(plan.expectedMinutes) || 0,
      actualMinutes: Number(plan.actualMinutes) || 0,
    }));

  return {
    periodEnd: studyDate,
    daysWithRecords: recordedDates.size,
    totalPlans: plans.length,
    subjects,
    attentionPlans,
  };
};

const parseAiPlanAnswer = (answer) => {
  const raw = String(answer || '').trim();
  const fenced = raw.match(/```(?:json)?\s*([\s\S]*?)```/i)?.[1];
  const candidate = (fenced || raw).match(/\{[\s\S]*\}/)?.[0];
  if (!candidate) return null;

  try {
    return JSON.parse(candidate);
  } catch {
    return null;
  }
};

const requestAiStudyPlan = async (plans, studyDate, historySummary) => {
  const planContext = toAiPlanContext(plans);
  const message = [
    `학습 날짜는 ${studyDate}입니다.`,
    `기존 학습 계획 JSON: ${JSON.stringify(planContext)}`,
    `최근 7일 학습 기록 요약 JSON: ${JSON.stringify(historySummary)}`,
    '기존 계획과 겹치지 않도록 다음 학습 계획 하나를 추천하세요.',
    '최근 7일의 완료율, 계획 대비 실제 학습 시간, 평균 이해도와 반복 미완료 항목을 함께 분석하세요.',
    '완료율과 이해도가 낮거나 실제 학습 시간이 부족한 과목을 우선하고 시간은 08:00~23:00 사이로 정하세요.',
    '기록이 7일 미만이면 현재까지 쌓인 기록과 오늘 계획을 기준으로 판단하세요.',
    '반드시 설명이나 마크다운 없이 다음 JSON 객체만 출력하세요:',
    '{"startTime":"HH:mm","endTime":"HH:mm","subject":"과목","planContent":"구체적인 학습 내용","memo":"추천 이유"}',
  ].join('\n');
  const response = await axios.post(
    `${process.env.REACT_APP_SERVER_URL}/aiStudy/ask`,
    { message, gradeLevel: 'all', answerMode: 'fast', history: [] },
    { timeout: 45000 },
  );
  return parseAiPlanAnswer(response.data?.answer);
};

export const createAiPlanDraft = async (plans, studyDate, recentPlans = []) => {
  const fallbackDraft = createFallbackDraft(plans, studyDate);
  const planContext = toAiPlanContext(plans);
  const historySummary = summarizeLearningHistory(recentPlans, studyDate);

  try {
    const response = await postWithAiBilling(
      'scheduler_plan', `${process.env.REACT_APP_SERVER_URL}/scheduler/ai-plan`,
      {
        studyDate,
        plans: planContext,
        history: historySummary,
      },
      { timeout: 45000 },
    );

    return normalizeAiDraft(response.data?.plan, plans, studyDate) || fallbackDraft;
  } catch (error) {
    if (isBillingUiEnabled() || error.response?.data?.errorCode?.startsWith('BILLING_')) throw error;
    try {
      const compatiblePlan = await requestAiStudyPlan(plans, studyDate, historySummary);
      const normalizedDraft = normalizeAiDraft(compatiblePlan, plans, studyDate);
      if (normalizedDraft) return normalizedDraft;
    } catch {
      // AI 경로를 모두 사용할 수 없으면 아래의 로컬 추천을 사용한다.
    }

    return { ...fallbackDraft, aiNotice: 'AI 연결이 원활하지 않아 학습 이력을 바탕으로 기본 계획을 추천했습니다.' };
  }
};
